/**
 * Readable labels for the raw unit codes stored in the price data
 * (material_instruments.unit, material_time_series.value_unit,
 * material_daily_summaries.value_unit).
 *
 * The importers write codes such as "brl_US", "bushel_US" or "troyounce".
 * They are fine as keys (unit conversion in the chart still works on the raw
 * codes) but must not be shown to users verbatim.
 *
 * Keys are lower-case; lookups are case-insensitive.
 */
export const UNIT_LABELS = {
  // mass
  mt: { short: 'mt', long: 'metric ton' },
  t: { short: 't', long: 'metric ton' },
  tonne: { short: 't', long: 'metric ton' },
  t_us: { short: 'short ton', long: 'short ton (US)' },
  st: { short: 'short ton', long: 'short ton (US)' },
  kg: { short: 'kg', long: 'kilogram' },
  g: { short: 'g', long: 'gram' },
  lb: { short: 'lb', long: 'pound' },
  lbs: { short: 'lb', long: 'pound' },
  oz: { short: 'oz', long: 'ounce' },
  troyounce: { short: 'troy oz', long: 'troy ounce' },
  troy_oz: { short: 'troy oz', long: 'troy ounce' },
  ozt: { short: 'troy oz', long: 'troy ounce' },
  // volume
  brl_us: { short: 'bbl', long: 'barrel' },
  brl: { short: 'bbl', long: 'barrel' },
  bbl: { short: 'bbl', long: 'barrel' },
  bushel_us: { short: 'bu', long: 'bushel' },
  bushel: { short: 'bu', long: 'bushel' },
  bu: { short: 'bu', long: 'bushel' },
  gal_us: { short: 'gal', long: 'gallon (US)' },
  gal: { short: 'gal', long: 'gallon' },
  l: { short: 'l', long: 'litre' },
  m3: { short: 'm³', long: 'cubic metre' },
  'mio. m³': { short: 'Mio. m³', long: 'million cubic metres' },
  'mio. m3': { short: 'Mio. m³', long: 'million cubic metres' },
  rm: { short: 'RM', long: 'stacked cubic metre' },
  // energy
  mmbtu: { short: 'MMBtu', long: 'million British thermal units' },
  thm: { short: 'therm', long: 'therm' },
  kwh: { short: 'kWh', long: 'kilowatt hour' },
  mwh: { short: 'MWh', long: 'megawatt hour' },
  gwh: { short: 'GWh', long: 'gigawatt hour' },
  twh: { short: 'TWh', long: 'terawatt hour' },
  // area / count / other
  sqft: { short: 'sq ft', long: 'square foot' },
  stk: { short: 'pc', long: 'piece' },
  pc: { short: 'pc', long: 'piece' },
  index: { short: 'index', long: 'index points' },
  '%': { short: '%', long: 'percent' },
};

// Values the backend uses for "no unit". They must not end up as "USD/n/a".
const EMPTY_UNITS = new Set(['', 'n/a', 'na', 'none', 'null', 'unit']);

const normaliseKey = (unit) => String(unit ?? '').trim().toLowerCase();

/** Short readable unit label, e.g. "brl_US" -> "bbl". Unknown codes are returned unchanged. */
export function formatUnit(unit) {
  const key = normaliseKey(unit);
  if (EMPTY_UNITS.has(key)) return '';
  const entry = UNIT_LABELS[key];
  return entry ? entry.short : String(unit).trim();
}

/** Long readable unit name, e.g. "brl_US" -> "barrel". Falls back to the short label. */
export function formatUnitLong(unit) {
  const key = normaliseKey(unit);
  if (EMPTY_UNITS.has(key)) return '';
  const entry = UNIT_LABELS[key];
  return entry ? entry.long : String(unit).trim();
}

// Werte fuer "keine Waehrung": /general liefert "n/a", andere Endpunkte null
// oder einen leeren String. Reihen wie Gasspeicher (GWh), Indizes oder Quoten
// haben nur eine Einheit; dort darf nie eine Waehrung (auch kein "USD") stehen.
const EMPTY_CURRENCIES = new Set(['', 'n/a', 'na', 'none', 'null', 'undefined', '-', '—']);

/** Waehrungscode in Grossbuchstaben, "" wenn keine Waehrung gesetzt ist. */
export function formatCurrency(currency) {
  const c = String(currency ?? '').trim();
  if (EMPTY_CURRENCIES.has(c.toLowerCase())) return '';
  return c.toUpperCase();
}

// Unterwaehrungen (Faktor 100 zur Basis): als Code bleiben sie USX/GBX/EUX
// (formatCurrency normalisiert nur), angezeigt werden sie als Cent/Pence.
const SUBUNIT_LABELS = { USX: 'US¢', GBX: 'GBp', EUX: '€ct' };

/** Anzeige-Label einer Waehrung: "usx" -> "US¢", "eur" -> "EUR", leer -> "". */
export function currencyLabel(currency) {
  const c = formatCurrency(currency);
  return SUBUNIT_LABELS[c] || c;
}

/** "USD" + "brl_US" -> "USD/bbl"; either side may be missing. */
export function formatPerUnit(currency, unit) {
  const cur = currencyLabel(currency);
  const u = formatUnit(unit);
  if (cur && u) return `${cur}/${u}`;
  return cur || u;
}

/**
 * Suffix fuer absolute Aenderungen ("-1.37% / -216.00 USD"): die Waehrung,
 * ohne Waehrung die Einheit ("-216.00 GWh"), ohne beides "".
 */
export function formatCurrencyOrUnit(currency, unit) {
  return currencyLabel(currency) || formatUnit(unit);
}

/** Bereits formatierte Zahl + "Waehrung/Einheit", z. B. "1,234.00 GWh". */
export function withPerUnit(formattedValue, currency, unit) {
  const suffix = formatPerUnit(currency, unit);
  return suffix ? `${formattedValue} ${suffix}` : String(formattedValue);
}

export default formatUnit;
